import type {
  AssessmentSessionState,
  CreateAssessmentSessionResponse,
  CreateSessionResponse,
  JoinAssessmentResponse,
  JoinSessionResponse,
} from "@radar-sim/protocol";
import type { Command, Scenario } from "@radar-sim/schema";
import type { Recording } from "@radar-sim/engine";

import { loadToken } from "./authToken.js";

/**
 * Thin client for the worker's REST API. The worker is hosted at the same
 * origin in production (Pages Functions style) or behind a configurable
 * base URL in dev. The API surface is small enough that hand-rolling the
 * fetch calls beats pulling in a client library.
 *
 * Auth: the web build uses the `goa_session` cookie
 * (`Domain=.goastern.com`) set by auth.goastern.com after sign-in —
 * every fetch goes out with `credentials: "include"` and the worker
 * reads the cookie. The Capacitor build can't get that cookie (the
 * WebView origin is `https://localhost`), so we additionally inject an
 * `Authorization: Bearer <jwt>` header from `loadToken()` — the worker
 * accepts either credential.
 */

function authHeaders(): Record<string, string> {
  const token = loadToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface AuthAPI {
  base: string;
}

export const DEFAULT_API: AuthAPI = {
  base:
    typeof window !== "undefined" && window.location.hostname !== "localhost"
      ? ""
      : (typeof window !== "undefined"
          ? (window as unknown as { __RADAR_SIM_API__?: string })
              .__RADAR_SIM_API__
          : undefined) ?? "http://localhost:8787",
};

async function postJson<T>(
  api: AuthAPI,
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(api.base + path, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${path} failed: ${res.status} ${text}`);
  }
  return (await res.json()) as T;
}

/// Returns the current signed-in user. The worker reads the
/// `goa_session` cookie set by auth.goastern.com (Domain=.goastern.com)
/// and replies with the verified email. Anonymous users get
/// `{authenticated: false}`.
///
/// The pre-iter-4 `requestMagicLink` / `verifyMagicLink` calls that
/// used to hit `/auth/login` and `/auth/verify` are gone — sign-in
/// lives at `auth.goastern.com` now, driven by `classroom/Login.tsx`'s
/// redirect.
export interface MeResponse {
  authenticated: boolean;
  email?: string;
  /** SKUs the user holds active entitlements for. Empty when
   *  anonymous or when the user is signed in but hasn't bought
   *  anything. Drives the demo-vs-full scenario gate in App.tsx. */
  skus: ReadonlyArray<string>;
}

export async function fetchMe(api: AuthAPI): Promise<MeResponse> {
  const res = await fetch(api.base + "/auth/me", {
    credentials: "include",
    headers: { ...authHeaders() },
  });
  if (!res.ok) return { authenticated: false, skus: [] };
  const body = (await res.json()) as Partial<MeResponse>;
  return {
    authenticated: body.authenticated === true,
    ...(body.email !== undefined ? { email: body.email } : {}),
    skus: body.skus ?? [],
  };
}

export async function createSession(
  api: AuthAPI,
  scenario: Scenario,
): Promise<CreateSessionResponse> {
  return postJson(api, "/sessions", {
    scenarioId: scenario.metadata.id,
    scenario,
  });
}

export async function joinSession(
  api: AuthAPI,
  joinCode: string,
): Promise<JoinSessionResponse> {
  return postJson(api, "/sessions/join", { joinCode });
}

export function buildSessionWebSocketUrl(
  api: AuthAPI,
  sessionId: string,
): string {
  const httpUrl = new URL(
    `${api.base.replace(/\/$/, "")}/sessions/${sessionId}/ws`,
    typeof window !== "undefined" ? window.location.href : "http://localhost",
  );
  httpUrl.protocol = httpUrl.protocol === "https:" ? "wss:" : "ws:";
  return httpUrl.toString();
}

export async function createAssessmentSession(
  api: AuthAPI,
  scenario: Scenario,
  enrolledStudentEmails: string[] = [],
): Promise<CreateAssessmentSessionResponse> {
  return postJson(api, "/assessment/sessions", {
    scenarioId: scenario.metadata.id,
    scenario,
    enrolledStudentEmails,
  });
}

export async function joinAssessmentSession(
  api: AuthAPI,
  joinCode: string,
): Promise<JoinAssessmentResponse> {
  return postJson(api, "/assessment/sessions/join", { joinCode });
}

export async function fetchAssessmentState(
  api: AuthAPI,
  assessmentId: string,
): Promise<AssessmentSessionState> {
  const res = await fetch(
    api.base + `/assessment/sessions/${assessmentId}/state`,
    { credentials: "include", headers: { ...authHeaders() } },
  );
  if (!res.ok) throw new Error(`assessment state ${res.status}`);
  return (await res.json()) as AssessmentSessionState;
}

export async function injectAssessmentCommand(
  api: AuthAPI,
  assessmentId: string,
  studentEmail: string,
  command: Command,
): Promise<{ ok: boolean }> {
  return postJson(
    api,
    `/assessment/sessions/${assessmentId}/inject/${encodeURIComponent(studentEmail)}`,
    { command },
  );
}

export async function fetchRecording(
  api: AuthAPI,
  sessionId: string,
): Promise<Recording> {
  const res = await fetch(api.base + `/sessions/${sessionId}/recording`, {
    credentials: "include",
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error(`recording fetch ${res.status}`);
  return (await res.json()) as Recording;
}
